'use client';

import React, { useState } from 'react';
import { Flex, Text, Box, VStack, HStack, Separator, copyToaster } from '@skin-seoul/design-system';
import { type TrackingEvent } from '@skin-seoul/service';
import { LucideIcon } from '../LucideIcon';
import { TrackingTimeline } from './TrackingTimeline';

interface OrderTrackingInfoProps {
  deliveryDate: string;
  trackingNumber: string;
  trackingEvents: TrackingEvent[];
}

const copyTrackingNumber = (text: string) => {
  navigator.clipboard.writeText(text);
  copyToaster.create({ title: 'Tracking number copied' });
};

export const OrderTrackingInfo = ({ deliveryDate, trackingNumber, trackingEvents }: OrderTrackingInfoProps) => {
  const hasTrackingEvents = trackingEvents && trackingEvents.length > 0;
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(hasTrackingEvents);

  const toggleTimeline = () => {
    setIsTimelineExpanded(!isTimelineExpanded);
  };

  return (
    <VStack gap={4} bg="neutral.50" borderRadius="8px" padding={3} width="100%">
      <Flex justifyContent="space-between" alignItems="flex-start" width="100%">
        <VStack gap={3} alignItems="flex-start">
          <VStack gap={1.25} alignItems="flex-start">
            <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
              Delivery Date
            </Text>
            <Text textStyle="label.medium" fontWeight="medium" color="neutral.700">
              {deliveryDate}
            </Text>
          </VStack>
          <HStack gap={1}>
            <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
              Tracking: {trackingNumber}
            </Text>
            <Box
              cursor="pointer"
              onClick={() => copyTrackingNumber(trackingNumber)}
              _hover={{ opacity: 0.8 }}
              width={4}
              height={4}
              color="neutral.400"
            >
              <LucideIcon name="Copy" width={4} height={4} />
            </Box>
          </HStack>
        </VStack>
        {hasTrackingEvents && (
          <HStack gap={1} cursor="pointer" _hover={{ opacity: 0.8 }} onClick={toggleTimeline}>
            <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
              Track
            </Text>
            <Box width={4} height={4} color="neutral.400">
              <LucideIcon name={isTimelineExpanded ? 'CircleMinus' : 'CirclePlus'} width={4} height={4} />
            </Box>
          </HStack>
        )}
      </Flex>

      {isTimelineExpanded && hasTrackingEvents && (
        <>
          <Separator width="100%" borderColor="neutral.100" />

          <TrackingTimeline events={trackingEvents} />
        </>
      )}
    </VStack>
  );
};
